
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

type ChatRoomMember = Database['public']['Tables']['chat_room_members']['Row'];

interface ChatRoomMemberWithProfile extends ChatRoomMember {
  profiles: {
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
  } | null;
}

export function useChatRoomMembers(roomId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const {
    data: members = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ["chat-room-members", roomId],
    queryFn: async (): Promise<ChatRoomMemberWithProfile[]> => {
      console.log("Fetching members for room:", roomId);
      
      const { data, error } = await supabase
        .from("chat_room_members")
        .select(`
          *,
          profiles:user_id (
            first_name,
            last_name,
            avatar_url
          )
        `)
        .eq("room_id", roomId);

      if (error) {
        console.error("Error fetching room members:", error);
        throw error;
      }

      console.log("Fetched members:", data);
      return data as ChatRoomMemberWithProfile[];
    },
    enabled: !!roomId,
    retry: 3,
    retryDelay: 1000
  });

  const addMemberMutation = useMutation({
    mutationFn: async ({ userId, role = 'member' }: { userId: string; role?: string }) => {
      if (!user) {
        throw new Error("Authentication required");
      }

      console.log("Adding member to room:", { roomId, userId, role });
      
      const { error } = await supabase
        .from("chat_room_members")
        .insert({
          room_id: roomId,
          user_id: userId,
          role
        });

      if (error) {
        console.error("Error adding member:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-room-members", roomId] });
      toast.success("Member added successfully");
    },
    onError: (error) => {
      console.error("Failed to add member:", error);
      toast.error("Failed to add member");
    }
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!user) {
        throw new Error("Authentication required");
      }

      console.log("Removing member from room:", { roomId, userId });
      
      const { error } = await supabase
        .from("chat_room_members")
        .delete()
        .eq("room_id", roomId)
        .eq("user_id", userId);

      if (error) {
        console.error("Error removing member:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-room-members", roomId] });
      toast.success("Member removed successfully");
    },
    onError: (error) => {
      console.error("Failed to remove member:", error);
      toast.error("Failed to remove member");
    }
  });

  const updateMemberRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: string }) => {
      if (!user) {
        throw new Error("Authentication required");
      }

      console.log("Updating member role:", { roomId, userId, role });
      
      const { error } = await supabase
        .from("chat_room_members")
        .update({ role })
        .eq("room_id", roomId)
        .eq("user_id", userId);

      if (error) {
        console.error("Error updating member role:", error);
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-room-members", roomId] });
      toast.success("Member role updated successfully");
    },
    onError: (error) => {
      console.error("Failed to update member role:", error);
      toast.error("Failed to update member role");
    }
  });

  return {
    members,
    isLoading,
    error,
    refetch,
    addMember: addMemberMutation.mutate,
    removeMember: removeMemberMutation.mutate,
    updateMemberRole: updateMemberRoleMutation.mutate,
    isAddingMember: addMemberMutation.isPending,
    isRemovingMember: removeMemberMutation.isPending,
    isUpdatingRole: updateMemberRoleMutation.isPending
  };
}
